import React, { useState, useEffect } from 'react'

import { usd } from 'src/components/I18n'
import CountUpWrapper from 'src/components/CountUpWrapper'
import renderI18nTemplate from 'src/utils/renderI18nTemplate'
import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import { injectLocalPortalModal } from '../injectLocalPortalModal'

import DonorFeed from './DonorFeed'

// Brand-tinted progress alert at the top of the widget. v6 used a
// white card with a top border separating it from the panels;
// v7's widget body has no internal borders, so this needs to read
// as visually distinct on its own — a brand-colored alert (same
// vocabulary as OrderSummary) does that without re-introducing the
// border-stack.
//
// Refetches the goal stats every 30s to catch concurrent donations
// from other donors, plus immediately when `donated` flips (the
// donor just completed their own donation and we want their
// contribution reflected before the thank-you transition settles).
// The fetch also returns the most recent orders + more-available
// flag (?with_orders=1); both feed straight into DonorFeed which
// shares this component's React tree so its useInfiniteOrders hook
// picks up new top rows automatically as we poll.

const getTotals = (data, showActiveTotals) => ({
  raised: showActiveTotals ? data.active_raised : data.hybrid_raised_with_matching,
  donors: showActiveTotals ? data.active_donors : data.total_donors,
})


const ProgressBar = ({ raised, goal }) => {
  const percent = Math.min(100, raised / goal * 100)
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin="0"
      aria-valuemax="100"
      className="h-2 bg-brand-100 rounded-full overflow-hidden"
    >
      <div
        className="h-full bg-brand-500 transition-[width] duration-300 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}

const Goal = ({
  i18n, partner, partnerItem, goalPartnerItem, loadedAsChild,
  goalThreshold, showActiveTotals, donated,
  blank_modal_src, widget_instance_token,
}) => {
  // Goal sourcing: the parent (goalPartnerItem) owns the goal in
  // newer parent/child IH fundraisers; only fall back to
  // partnerItem when the donor explicitly loaded a child URL —
  // see DonationsController#set_loaded_as_child for the rationale
  // and the controller-side logic. Totals are always sourced from
  // partnerItem because init_multi_orgs already collapsed it to
  // the right granularity (the single child for single-org parent
  // URLs, the parent for multi-org splits, the child itself for
  // child URLs).
  const goal = (loadedAsChild ? partnerItem : goalPartnerItem).goal

  const [ progress, setProgress ] = useState(() =>
    getTotals(partnerItem, showActiveTotals),
  )
  // Donor feed state, populated on first fetch. null until then so
  // we can keep the donor count out of clickable mode until the data
  // is ready — first fetch is fast enough that this gap is invisible
  // in practice. The modal itself only renders once openPortalModal
  // has resolved a portal target (donor clicked the count, modal is
  // open), so the iframe never mounts speculatively.
  const [ orders, setOrders ] = useState(null)
  const [ moreOrdersAvailable, setMoreOrdersAvailable ] = useState(false)
  const [ donorFeedPortal, setDonorFeedPortal ] = useState(null)

  const openDonorFeed = async () => {
    const result = await openPortalModal({
      // widget_instance_token suffix sidesteps the cache-hit /
      // portalReady race when two donate widgets coexist on the page
      // — each gets its own modal iframe in mode 2.
      id: `donor-feed:${widget_instance_token}`,
      src: blank_modal_src,
      ariaLabel: i18n.t('goal.recent_donations'),
      maxWidth: '30rem',
      // 'host' only — in mode 2 the iframe grows tall and would bury
      // the close button below the fold, so cap it + scroll the list
      // inside. In modes 1/3 the widget-modal fills the viewport in
      // the donate widget's own frame and scrolls naturally.
      scrollWithin: 'host',
      renderLocal: injectLocalPortalModal,
    })
    setDonorFeedPortal(result)
  }

  const url = `/widgets/goal/${partner.key}/${partnerItem.external_id}?with_orders=1`

  const fetchProgress = async () => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) return
    const { orders, more_orders_available, ...json } = await response.json()
    setProgress(getTotals(json, showActiveTotals))
    setOrders(orders)
    setMoreOrdersAvailable(more_orders_available)
  }

  // Initial fetch + refetch on donor's own completion. The mount
  // fetch picks up any drift between page load and React mount.
  useEffect(() => { fetchProgress() }, [ donated ])

  // Periodic refresh for concurrent donations from other donors.
  useEffect(() => {
    const interval = setInterval(fetchProgress, 30000)
    return () => clearInterval(interval)
  }, [])

  const raised = Number(progress.raised)
  const numericGoal = Number(goal)

  // No progress yet — generic prompt copy. Below the goal_threshold,
  // a slightly punchier "fundraising is heating up" variant. With a
  // goal set, both branches collapse into the goal-vs-raised display
  // so the donor sees the bar even at small early amounts.
  const content = (() => {
    if (!raised) return <p>{i18n.t('goal_raised_none')}</p>
    if (!numericGoal && raised < Number(goalThreshold)) {
      return <p>{i18n.t('goal_raised_little')}</p>
    }

    // Donor count is a button only once we have orders to show
    // (first fetch resolved). Before that, render as plain text so
    // an early click doesn't try to open an unmounted modal.
    //
    // i18n.pluralize defers form selection to Intl.PluralRules so
    // Slavic / Arabic / Romanian (and any future locale with non-
    // binary plural categories) get the right grammatical form
    // instead of collapsing to one/other.
    const donorsLabel = i18n.pluralize('goal.donors', progress.donors)
    const donorCount = orders ? (
      <button
        type="button"
        onClick={openDonorFeed}
        className="btn-link whitespace-nowrap font-semibold underline underline-offset-2"
      >
        {donorsLabel}
      </button>
    ) : (
      <span className="whitespace-nowrap font-semibold">
        {donorsLabel}
      </span>
    )
    const raisedAmount = (
      <CountUpWrapper
        className="font-semibold"
        amount={progress.raised}
        prefix={usd.symbol}
      />
    )

    if (!numericGoal) {
      return (
        <p>
          {renderI18nTemplate(i18n.t('goal.raised_by'), {
            amount: raisedAmount,
            donors: donorCount,
          })}
        </p>
      )
    }

    return (
      <div className="space-y-2">
        <p>
          {renderI18nTemplate(i18n.t('goal.raised_by_with_goal'), {
            amount: raisedAmount,
            goal: i18n.formatCurrency(goal, usd),
            donors: donorCount,
          })}
        </p>
        <ProgressBar raised={raised} goal={numericGoal} />
      </div>
    )
  })()

  return (
    <>
      <div className="rounded-lg border border-brand-500 bg-brand-50 p-3 @roomy:p-4 text-sm">
        {content}
      </div>
      {orders && donorFeedPortal && (
        <DonorFeed
          portal={donorFeedPortal}
          i18n={i18n}
          orders={orders}
          moreOrdersAvailable={moreOrdersAvailable}
          feedUrl={url}
        />
      )}
    </>
  )
}

export default Goal
